import { create } from 'zustand';
import { AppNode, Connection, NodeStatus, NodeType } from '../types';
import type { Editor } from '@tiptap/react';

// 编辑器实例缓存（不参与zustand响应式，避免频繁重渲染）
const editorCache = new Map<string, Editor>();
export const getEditorInstance = (nodeId: string) => editorCache.get(nodeId);
export const setEditorInstance = (nodeId: string, editor: Editor | null) => {
  if (editor) {
    editorCache.set(nodeId, editor);
  } else {
    editorCache.delete(nodeId);
  }
};

export interface NodeState {
  nodes: AppNode[];
  connections: Connection[];
  nodeMap: Map<string, AppNode>;
  addNode: (node: AppNode) => void;
  updateNode: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string, status?: NodeStatus) => void;
  deleteNodes: (ids: string[]) => void;
  addConnection: (from: string, to: string, targetHandle?: string) => void;
  removeConnection: (from: string, to: string, targetHandle?: string) => void;
  setNodes: (nodes: AppNode[]) => void;
  setConnections: (connections: Connection[]) => void;
  /** 加载会话开始：记录当前节点集合为基线，飞行期间新建的活节点在 setNodes/setConnections 覆盖时受保护 */
  beginBatchLoad: () => void;
  /** 加载会话结束：解除基线保护（并发加载按计数配对） */
  endBatchLoad: () => void;
  duplicateNode: (id: string) => AppNode | null;
  _updateNodePosition: (id: string, x: number, y: number) => void;
  _updateNodeGroupId: (id: string, groupId: string | undefined) => void;
  _updateNodeParent: (id: string, parentNode: string | undefined, extent?: AppNode['extent'], expandParent?: boolean) => void;
  _batchUpdateNodes: (updates: Array<{ id: string; changes: Partial<AppNode> }>) => void;
  // P2-1: 局部更新节点位置，保持未变动节点引用不变
  // 之前 setNodes(finalNodes) 会创建新数组，导致 useInputAssets 的 useShallow 判定不等，
  // 触发所有 GenerateNode 重渲染。此方法只更新被拖拽节点，未拖拽节点引用不变。
  updateNodePositions: (updates: Array<{ id: string; x: number; y: number }>) => void;
  updateNodeDataField: (id: string, field: string, value: unknown) => void;
  updateNodesBatch: (updates: Array<{ id: string; data: Partial<AppNode['data']> }>) => void;
  _removeNodesByGroupId: (groupId: string) => void;
  getNodeById: (id: string) => AppNode | undefined;
  getNodesByGroupId: (groupId: string) => AppNode[];
  getNodesInBounds: (x: number, y: number, width: number, height: number) => AppNode[];

  // 文本节点专用方法
  updateNodeSubMode: (id: string, subMode: import('../types').PromptSubMode) => void;
  updateNodeEditMode: (id: string, editMode: import('../types').TextEditMode) => void;
  updateNodeSize: (id: string, size: import('../types').NodeSize) => void;
  updateNodeRichContent: (id: string, richContent: import('../types').RichTextContent, plainText: string) => void;
  getNextTextNodeIndex: () => number;

  // 脚本节点专用：data.script 为嵌套结构，updateNode 浅合并会让步骤内编辑整包覆盖，
  // 此 action 以函数式更新保证不可变替换（只换 script 引用，其余字段引用不变）
  updateScript: (id: string, updater: (script: import('../types/scriptNode').ScriptData) => import('../types/scriptNode').ScriptData) => void;

  // 🔴 P0-1: 待生成画布节点管理（迁移自 streamState.pendingNodeMap + currentPendingNodeId）
  // 非响应式：Map 与单值均通过 action 命令式访问，无组件订阅
  pendingGenNodes: Map<string, string>;
  currentPendingNodeId: string | null;
  setPendingGenNode: (taskKey: string, nodeId: string) => void;
  getPendingGenNode: (taskKey: string) => string | undefined;
  deletePendingGenNode: (taskKey: string) => void;
  deletePendingGenNodesByNodeId: (nodeId: string) => void;
  clearPendingGenNodes: () => void;
  setCurrentPendingNodeId: (nodeId: string | null) => void;
  getCurrentPendingNodeId: () => string | null;
}

function rebuildNodeMap(nodes: AppNode[]): Map<string, AppNode> {
  const map = new Map<string, AppNode>();
  for (const n of nodes) {
    map.set(n.id, n);
  }
  return map;
}

// 加载会话守卫状态：异步加载（loadWorkflow/loadDataFromStorage）在飞行期间持有旧快照，
// 返回后 setNodes/setConnections 全量覆盖会把「飞行期间本地新建的节点」抹掉
// （批量生图/分屏生成节点约 1 秒后消失、后端仍在执行的根因）。
// beginBatchLoad 在 await 前记录基线，覆盖时把「基线之外、状态 WORKING/QUEUED」的活节点
// 及其连线合并回快照（任务已提交后端，节点不能丢）；并发加载按计数配对、基线取并集
let loadSessionCount = 0;
let loadBaselineIds: Set<string> | null = null;

export const useNodeStore = create<NodeState>((set, get) => ({
  nodes: [],
  connections: [],
  nodeMap: new Map(),
  // 🔴 P0-1: 迁移自 streamState.pendingNodeMap + currentPendingNodeId（非响应式容器）
  pendingGenNodes: new Map<string, string>(),
  currentPendingNodeId: null,

  addNode: (node) => set((state) => {
    if (!state?.nodes) return state;
    const newNodes = [...state.nodes, node];
    const newMap = new Map(state.nodeMap);
    newMap.set(node.id, node);
    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNode: (id, data, size, title, status) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, ...data }, title: title || oldNode.title };
    if (size?.width) updated.width = size.width;
    if (size?.height) updated.height = size.height;
    if (status) updated.status = status;

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  deleteNodes: (ids) => {
    set((state) => {
      if (!state?.nodes) return state;
      const idSet = new Set(ids);
      const newNodes = state.nodes.filter((n) => !idSet.has(n.id));
      const newMap = rebuildNodeMap(newNodes);
      return {
        nodes: newNodes,
        nodeMap: newMap,
        connections: state.connections.filter((c) => !idSet.has(c.from) && !idSet.has(c.to)),
      };
    });
  },

  addConnection: (from, to, targetHandle) => set((state) => {
    if (!state?.nodes) return state;
    if (state.connections.some((c) => c.from === from && c.to === to && (c.targetHandle ?? '') === (targetHandle ?? ''))) {
      return state;
    }

    const conn: Connection = targetHandle ? { from, to, targetHandle } : { from, to };

    const targetIndex = state.nodes.findIndex(n => n.id === to);
    if (targetIndex === -1) {
      return {
        connections: [...state.connections, conn],
      };
    }

    const targetNode = state.nodes[targetIndex];
    const updatedTarget = { ...targetNode, inputs: [...targetNode.inputs, from] };
    if (targetNode.type === NodeType.IMAGE_GENERATOR && (!targetNode.data.generationMode || targetNode.data.generationMode === 'TXT2IMG')) {
      updatedTarget.data = { ...updatedTarget.data, generationMode: 'IMG2IMG' };
    }

    const newNodes = [...state.nodes];
    newNodes[targetIndex] = updatedTarget;

    const newMap = new Map(state.nodeMap);
    newMap.set(to, updatedTarget);

    return {
      connections: [...state.connections, conn],
      nodes: newNodes,
      nodeMap: newMap,
    };
  }),

  removeConnection: (from, to, targetHandle) => set((state) => {
    if (!state?.nodes) return state;
    const shouldRemove = (c: Connection) =>
      c.from === from && c.to === to && (targetHandle === undefined || (c.targetHandle ?? '') === targetHandle);
    const targetIndex = state.nodes.findIndex(n => n.id === to);
    if (targetIndex === -1) {
      return {
        connections: state.connections.filter((c) => !shouldRemove(c)),
      };
    }

    const removedCount = state.connections.filter(shouldRemove).length;
    const targetNode = state.nodes[targetIndex];
    let pending = removedCount;
    const nextInputs = targetNode.inputs.filter((i) => {
      if (i === from && pending > 0) {
        pending -= 1;
        return false;
      }
      return true;
    });
    const updatedTarget = { ...targetNode, inputs: nextInputs };

    const newNodes = [...state.nodes];
    newNodes[targetIndex] = updatedTarget;

    const newMap = new Map(state.nodeMap);
    newMap.set(to, updatedTarget);

    return {
      connections: state.connections.filter((c) => !shouldRemove(c)),
      nodes: newNodes,
      nodeMap: newMap,
    };
  }),

  // 全量覆盖守卫：加载会话在途时，快照遗漏的「基线之外活节点」（WORKING/QUEUED）合并回快照，
  // 防止异步加载旧快照抹掉飞行期间新建的生成节点
  setNodes: (nodes) => set((state) => {
    if (loadBaselineIds && state.nodes.length > 0) {
      const snapshotIds = new Set(nodes.map(n => n.id));
      const orphans = state.nodes.filter(n =>
        !loadBaselineIds!.has(n.id) && !snapshotIds.has(n.id) &&
        (n.status === NodeStatus.WORKING || n.status === NodeStatus.QUEUED));
      if (orphans.length > 0) nodes = [...nodes, ...orphans];
    }
    return { nodes, nodeMap: rebuildNodeMap(nodes) };
  }),

  // 与 setNodes 守卫配套：快照遗漏的、两端涉及活节点（WORKING/QUEUED）的连线合并回快照，
  // 防止新生成节点与上游分块节点的连线被加载覆盖抹掉
  setConnections: (connections) => set((state) => {
    const aliveIds = new Set(
      state.nodes
        .filter(n => n.status === NodeStatus.WORKING || n.status === NodeStatus.QUEUED)
        .map(n => n.id),
    );
    if (aliveIds.size === 0) return { connections };
    const key = (c: Connection) => `${c.from}->${c.to}:${c.targetHandle ?? ''}`;
    const keys = new Set(connections.map(key));
    const orphans = state.connections.filter(c =>
      (aliveIds.has(c.from) || aliveIds.has(c.to)) && !keys.has(key(c)));
    return orphans.length > 0 ? { connections: [...connections, ...orphans] } : { connections };
  }),

  beginBatchLoad: () => {
    const current = new Set(get().nodes.map(n => n.id));
    loadBaselineIds = loadBaselineIds ? new Set([...loadBaselineIds, ...current]) : current;
    loadSessionCount += 1;
  },
  endBatchLoad: () => {
    loadSessionCount = Math.max(0, loadSessionCount - 1);
    if (loadSessionCount === 0) loadBaselineIds = null;
  },

  duplicateNode: (id) => {
    const state = get();
    const node = state.nodeMap.get(id);
    if (!node) return null;

    const newNode: AppNode = {
      ...node,
      id: `${node.id}-copy-${Date.now()}`,
      x: node.x + 50,
      y: node.y + 50,
      inputs: [],
    };

    const newNodes = [...state.nodes, newNode];
    const newMap = new Map(state.nodeMap);
    newMap.set(newNode.id, newNode);
    set({ nodes: newNodes, nodeMap: newMap });
    return newNode;
  },

  _updateNodePosition: (id, x, y) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], x, y };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _updateNodeGroupId: (id, groupId) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], groupId };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _updateNodeParent: (id, parentNode, extent, expandParent) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], parentNode, extent, expandParent };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _batchUpdateNodes: (updates) => set((state) => {
    if (!state?.nodes) return state;
    const updateMap = new Map(updates.map(u => [u.id, u.changes]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const changes = updateMap.get(n.id);
      if (changes) {
        changed = true;
        return { ...n, ...changes };
      }
      return n;
    });

    if (!changed) return state;

    const newNodeMap = rebuildNodeMap(newNodes);
    return { nodes: newNodes, nodeMap: newNodeMap };
  }),

  // P2-1: 局部更新节点位置，只替换被拖拽节点的对象引用
  // 未拖拽节点保持原引用，useShallow 比较时判定相等，不触发重渲染
  updateNodePositions: (updates) => set((state) => {
    if (!state?.nodes || updates.length === 0) return state;
    const updateMap = new Map(updates.map(u => [u.id, { x: u.x, y: u.y }]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const pos = updateMap.get(n.id);
      if (pos && (n.x !== pos.x || n.y !== pos.y)) {
        changed = true;
        return { ...n, x: pos.x, y: pos.y };
      }
      return n;
    });

    if (!changed) return state;

    // 只更新被拖拽节点的 map 条目，避免全量重建
    const newMap = new Map(state.nodeMap);
    for (const update of updates) {
      const node = newMap.get(update.id);
      if (node && (node.x !== update.x || node.y !== update.y)) {
        newMap.set(update.id, { ...node, x: update.x, y: update.y });
      }
    }
    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeDataField: (id, field, value) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    if ((oldNode.data as Record<string, unknown>)[field] === value) return state;

    const updated = { ...oldNode, data: { ...oldNode.data, [field]: value } };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodesBatch: (updates) => set((state) => {
    if (!state?.nodes) return state;
    const updateMap = new Map(updates.map(u => [u.id, u.data]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const dataUpdate = updateMap.get(n.id);
      if (dataUpdate) {
        changed = true;
        return { ...n, data: { ...n.data, ...dataUpdate } };
      }
      return n;
    });

    if (!changed) return state;

    const newNodeMap = rebuildNodeMap(newNodes);
    return { nodes: newNodes, nodeMap: newNodeMap };
  }),

  _removeNodesByGroupId: (groupId) => set((state) => {
    if (!state?.nodes) return state;
    const newNodes = state.nodes.filter(n => n.groupId !== groupId);
    return { nodes: newNodes, nodeMap: rebuildNodeMap(newNodes) };
  }),

  getNodeById: (id) => get().nodeMap.get(id),

  getNodesByGroupId: (groupId) => get().nodes.filter(n => n.groupId === groupId),

  getNodesInBounds: (x, y, width, height) => {
    return get().nodes.filter(n => {
      const nw = n.width ?? 280;
      const nh = n.height ?? 200;
      return n.x >= x && n.x + nw <= x + width && n.y >= y && n.y + nh <= y + height;
    });
  },

  // 文本节点专用方法
  updateNodeSubMode: (id, subMode) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, subMode } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeEditMode: (id, editMode) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, editMode } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeSize: (id, size) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, width: size.width, height: size.height, data: { ...oldNode.data, nodeSize: size } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeRichContent: (id, richContent, plainText) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, richContent, textContent: plainText } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  getNextTextNodeIndex: () => {
    const state = get();
    const textNodes = state.nodes.filter(n => n.data.textNodeIndex !== undefined);
    const maxIndex = textNodes.length > 0
      ? Math.max(...textNodes.map(n => n.data.textNodeIndex || 0))
      : 0;
    return maxIndex + 1;
  },

  updateScript: (id, updater) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const currentScript = (oldNode.data as { script?: import('../types/scriptNode').ScriptData }).script;
    if (!currentScript) return state;
    const nextScript = updater(currentScript);
    // 引用未变则跳过更新，避免无谓的节点重渲染
    if (nextScript === currentScript) return state;

    const updated = { ...oldNode, data: { ...oldNode.data, script: nextScript } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  // 🔴 P0-1: 待生成画布节点管理（迁移自 streamState.pendingNodeMap + currentPendingNodeId）
  // 非响应式：pendingGenNodes 直接 mutate Map 实例（不调用 set()），避免无谓重渲染；
  //   streamState.pendingNodeMap 与本字段共享同一 Map 引用，调用方无需修改。
  //   currentPendingNodeId 走标准 set()（无组件订阅，不会触发重渲染）。
  setPendingGenNode: (taskKey, nodeId) => {
    get().pendingGenNodes.set(taskKey, nodeId);
  },
  getPendingGenNode: (taskKey) => get().pendingGenNodes.get(taskKey),
  deletePendingGenNode: (taskKey) => {
    get().pendingGenNodes.delete(taskKey);
  },
  deletePendingGenNodesByNodeId: (nodeId) => {
    const map = get().pendingGenNodes;
    for (const [key, value] of map.entries()) {
      if (value === nodeId) map.delete(key);
    }
  },
  clearPendingGenNodes: () => {
    get().pendingGenNodes.clear();
  },
  setCurrentPendingNodeId: (nodeId) => set({ currentPendingNodeId: nodeId }),
  getCurrentPendingNodeId: () => get().currentPendingNodeId,
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
